import React, { useState } from 'react';
import { Sparkles, MapPin, Info } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
interface ProfileTabProps {
  theme: ZodiacTheme;
  birthData: {
    date: string;
    time: string;
    place: string;
  };
  setBirthData: (data: {
    date: string;
    time: string;
    place: string;
  }) => void;
  setActiveTab: (tab: string) => void;
}
export function ProfileTab({
  theme,
  birthData,
  setBirthData,
  setActiveTab
}: ProfileTabProps) {
  const [isEditingAstro, setIsEditingAstro] = useState(false);
  const [tempData, setTempData] = useState(birthData);
  const {
    success
  } = useToast();
  const handleSaveAstroData = () => {
    setBirthData(tempData);
    setIsEditingAstro(false);
    success('Астрологические данные сохранены. Ваш профиль обновлен!');
  };
  return <div className="space-y-6" data-id="element-17836">
      <div className="flex justify-between items-center mb-6" data-id="element-17837">
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-17838">
          Астрологический профиль
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-17839">
        {/* Left Col: Zodiac Card */}
        <div className="lg:col-span-1" data-id="element-17840">
          <Card padding="none" className={`overflow-hidden border-2 ${theme.border} shadow-lg`} data-id="element-17841">
            <div className={`h-32 bg-gradient-to-br ${theme.gradient} flex items-center justify-center relative`} data-id="element-17842">
              <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-17843"></div>
              <span className="text-6xl text-white drop-shadow-md" data-id="element-17844">
                {theme.icon}
              </span>
            </div>
            <div className="p-6 text-center" data-id="element-17845">
              <h3 className="text-2xl font-bold text-slate-900 mb-1" data-id="element-17846">
                {theme.name}
              </h3>
              <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${theme.bg} ${theme.text} mb-4`} data-id="element-17847">
                <Sparkles className="w-3 h-3" data-id="element-17848" /> {theme.element}
              </div>
              <p className="text-sm text-slate-600 mb-6" data-id="element-17849">
                Ваши данные используются для автоматического расчета транзитов,
                соляров и персонального календаря.
              </p>
              <div className="space-y-3 text-left bg-slate-50 p-4 rounded-xl" data-id="element-17850">
                <div className="flex justify-between text-sm" data-id="element-17851">
                  <span className="text-slate-500" data-id="element-17852">Дата:</span>
                  <span className="font-medium text-slate-900" data-id="element-17853">
                    {birthData.date || 'Не указана'}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-17854">
                  <span className="text-slate-500" data-id="element-17855">Время:</span>
                  <span className="font-medium text-slate-900" data-id="element-17856">
                    {birthData.time || 'Не указано'}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-17857">
                  <span className="text-slate-500" data-id="element-17858">Место:</span>
                  <span className="font-medium text-slate-900 truncate max-w-[120px]" title={birthData.place} data-id="element-17859">
                    {birthData.place || 'Не указано'}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Right Col: Edit Form */}
        <div className="lg:col-span-2" data-id="element-17860">
          <Card padding="lg" data-id="element-17861">
            <div className="flex justify-between items-center mb-6" data-id="element-17862">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-17863">
                Данные рождения
              </h3>
              {!isEditingAstro && <Button variant="outline" size="sm" onClick={() => setIsEditingAstro(true)} data-id="element-17864">
                  Изменить
                </Button>}
            </div>

            {isEditingAstro ? <div className="space-y-5 animate-in fade-in" data-id="element-17865">
                <div className="bg-blue-50 text-blue-700 p-4 rounded-xl text-sm flex gap-3 mb-6" data-id="element-17866">
                  <Info className="w-5 h-5 flex-shrink-0" data-id="element-17867" />
                  <p data-id="element-17868">
                    Точное время и место рождения критически важны для
                    правильного расчета асцендента и домов гороскопа.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-17869">
                  <Input label="Дата рождения" type="date" value={tempData.date} onChange={e => setTempData({
                ...tempData,
                date: e.target.value
              })} data-id="element-17870" />
                  <Input label="Время рождения" type="time" value={tempData.time} onChange={e => setTempData({
                ...tempData,
                time: e.target.value
              })} data-id="element-17871" />
                </div>
                <Input label="Место рождения (Город)" placeholder="Например: Москва, Россия" value={tempData.place} onChange={e => setTempData({
              ...tempData,
              place: e.target.value
            })} icon={<MapPin className="w-4 h-4" data-id="element-17873" />} data-id="element-17872" />

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-17874">
                  <Button variant="ghost" onClick={() => {
                setTempData(birthData);
                setIsEditingAstro(false);
              }} data-id="element-17875">
                    Отмена
                  </Button>
                  <Button variant="primary" onClick={handleSaveAstroData} data-id="element-17876">
                    Сохранить данные
                  </Button>
                </div>
              </div> : <div className="space-y-6" data-id="element-17877">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-17878">
                  <div className="p-4 border border-slate-100 rounded-xl bg-slate-50" data-id="element-17879">
                    <div className="text-xs text-slate-500 mb-1" data-id="element-17880">
                      Солнечный знак
                    </div>
                    <div className="font-bold text-slate-900 text-lg flex items-center gap-2" data-id="element-17881">
                      {theme.icon} {theme.name}
                    </div>
                  </div>
                  <div className="p-4 border border-slate-100 rounded-xl bg-slate-50" data-id="element-17882">
                    <div className="text-xs text-slate-500 mb-1" data-id="element-17883">
                      Лунный знак
                    </div>
                    <div className="font-bold text-slate-900 text-lg flex items-center gap-2" data-id="element-17884">
                      ☽ Скорпион
                    </div>
                  </div>
                  <div className="p-4 border border-slate-100 rounded-xl bg-slate-50" data-id="element-17885">
                    <div className="text-xs text-slate-500 mb-1" data-id="element-17886">Асцендент</div>
                    <div className="font-bold text-slate-900 text-lg flex items-center gap-2" data-id="element-17887">
                      ↑ Лев
                    </div>
                  </div>
                </div>

                <div className={`p-5 rounded-xl bg-gradient-to-r ${theme.lightGradient} border ${theme.border}`} data-id="element-17888">
                  <h4 className="font-bold text-slate-900 mb-2" data-id="element-17889">
                    Краткая характеристика
                  </h4>
                  <p className="text-sm text-slate-700 leading-relaxed" data-id="element-17890">
                    Как типичный представитель знака {theme.name}, вы обладаете
                    сильной интуицией и целеустремленностью. Ваша натальная
                    карта указывает на выраженные лидерские качества (Асцендент
                    во Льве), которые гармонично сочетаются с глубокой
                    эмоциональностью (Луна в Скорпионе).
                  </p>
                  <Button variant="outline" className="mt-4 bg-white" onClick={() => setActiveTab('reports')} data-id="element-17891">
                    Смотреть полный разбор
                  </Button>
                </div>
              </div>}
          </Card>
        </div>
      </div>
    </div>;
}